import Roles from "@app/user/roles";

export interface UnprocessedProviderChangeFlags {
  hasTargets: boolean;
  hasExcludes: boolean;
  hasTerminations: boolean;
}

export interface ProviderChangeRoleGateResult {
  hasRequiredRoles: boolean;
  missingRoleReasons: string[];
}

/**
 * Targets and Exclusions each require Roles.PROJECTION_PROCESSOR plus the matching
 * Roles.PROVIDER_TARGET_USER / Roles.PROVIDER_EXCLUDE_USER role, checked independently;
 * Terminations require Roles.TERM_PROVIDER_ADMIN.
 *
 * If a caller cannot distinguish targets from exclusions (only a combined "has unprocessed
 * projection providers" flag), pass that same flag as both hasTargets and hasExcludes — this
 * correctly requires BOTH PROVIDER_TARGET_USER and PROVIDER_EXCLUDE_USER rather than either alone.
 */
export const getProviderChangeRoleGate = (roles: string[], flags: UnprocessedProviderChangeFlags): ProviderChangeRoleGateResult => {
  const hasProjectionProcessorRole = roles.includes(Roles.PROJECTION_PROCESSOR);
  const missingRoleReasons: string[] = [];

  if (flags.hasTargets && !(hasProjectionProcessorRole && roles.includes(Roles.PROVIDER_TARGET_USER))) {
    missingRoleReasons.push("You do not have permission to process unprocessed targeted providers.");
  }

  if (flags.hasExcludes && !(hasProjectionProcessorRole && roles.includes(Roles.PROVIDER_EXCLUDE_USER))) {
    missingRoleReasons.push("You do not have permission to process unprocessed excluded providers.");
  }

  if (flags.hasTerminations && !roles.includes(Roles.TERM_PROVIDER_ADMIN)) {
    missingRoleReasons.push("You do not have permission to process unprocessed provider terminations.");
  }

  return { hasRequiredRoles: missingRoleReasons.length === 0, missingRoleReasons };
};
